import { AdminAttendeeAvatar } from '@/features/photos/PhotoAvatars'
import { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type AttendeeListItemDto, type AttendeeDetailDto } from '@/api/admin'
import { certificateDownloadUrl } from '@/api/public'
import { useTopBar } from '@/contexts/TopBarContext'
import { Breadcrumb } from '@/components/shared/Breadcrumb'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Mail, Phone, Building2, Check, GitMerge, X, Search, Download, AlertTriangle, ArrowLeft, ArrowRight } from 'lucide-react'
import { useAdminDates } from '@/hooks/useDates'

function AttendanceBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    Present:     'bg-green-100 text-green-700',
    Absent:      'bg-red-100 text-red-700',
    Failed:      'bg-amber-100 text-amber-700',
    NotRecorded: 'bg-gray-100 text-gray-500',
  }
  return <span className={`text-xs rounded-full px-2 py-0.5 ${map[status] ?? 'bg-gray-100 text-gray-500'}`}>{status === 'NotRecorded' ? 'Not recorded' : status}</span>
}

/** One side of the merge comparison: what's kept vs. what's about to be deleted. */
function MergeSideCard({ label, attendee, destructive }: { label: string; attendee: AttendeeDetailDto; destructive?: boolean }) {
  const { formatDate } = useAdminDates()
  return (
    <div className={`rounded-lg border px-3 py-2.5 text-xs ${destructive ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
      <p className={`font-semibold mb-1.5 ${destructive ? 'text-red-700' : 'text-emerald-700'}`}>{label}</p>
      <p className="font-medium text-gray-900 truncate">{attendee.fullName}</p>
      <p className="text-gray-500 truncate">{attendee.email}</p>
      {attendee.phone && <p className="text-gray-500 truncate">{attendee.phone}</p>}
      {attendee.companyName && <p className="text-gray-500 truncate">{attendee.companyName}</p>}
      <div className="mt-1.5 pt-1.5 border-t border-black/5 space-y-0.5 text-gray-500">
        <p>{attendee.bookings.length} booking{attendee.bookings.length === 1 ? '' : 's'}</p>
        <p>{attendee.certificates.length} certificate{attendee.certificates.length === 1 ? '' : 's'}</p>
        <p>{attendee.marketingConsent ? 'Marketing consent given' : 'No marketing consent'}</p>
        <p>{attendee.photoUpdatedAt ? 'Has a photo' : 'No photo'}</p>
        <p>Created {formatDate(attendee.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
      </div>
    </div>
  )
}

function MergeModal({ target, onClose }: { target: AttendeeDetailDto; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [error, setError] = useState('')
  const [candidateId, setCandidateId] = useState<string | null>(null)

  const { data: all = [] } = useQuery({
    queryKey: ['attendees'],
    queryFn: () => adminApi.getAttendees().then(r => r.data),
  })

  const candidates = useMemo(() => {
    const q = search.toLowerCase().trim()
    return all
      .filter(a => a.id !== target.id)
      .filter(a => !q || [a.fullName, a.email, a.companyName ?? ''].some(v => v.toLowerCase().includes(q)))
      .slice(0, 8)
  }, [all, search, target.id])

  const { data: source, isLoading: sourceLoading } = useQuery({
    queryKey: ['attendee', candidateId],
    queryFn: () => adminApi.getAttendee(candidateId!).then(r => r.data),
    enabled: !!candidateId,
  })

  const overlappingCourses = useMemo(() => {
    if (!source) return []
    const targetCourses = new Set(target.bookings.map(b => b.courseName))
    return [...new Set(source.bookings.filter(b => targetCourses.has(b.courseName)).map(b => b.courseName))]
  }, [source, target.bookings])

  const merge = useMutation({
    mutationFn: (sourceId: string) => adminApi.mergeAttendee(target.id, sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendees'] })
      queryClient.invalidateQueries({ queryKey: ['attendee', target.id] })
      onClose()
    },
    onError: () => setError('Failed to merge. Please try again.'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <GitMerge size={16} className="text-indigo-500" /> Merge duplicate
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        {!candidateId ? (
          <div className="p-5">
            <p className="text-sm text-gray-500 mb-3">
              Pick the duplicate record to merge into <strong>{target.fullName}</strong>. You'll get a chance to
              review what moves and what's deleted before anything happens.
            </p>
            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search attendees…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
              {candidates.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">No other attendees found.</p>
              )}
              {candidates.map((a: AttendeeListItemDto) => (
                <div key={a.id} className="flex items-center justify-between px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{a.fullName}</p>
                    <p className="text-xs text-gray-400 truncate">{a.email}</p>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => { setError(''); setCandidateId(a.id) }}>
                    Review <ArrowRight size={12} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-5">
            <button
              type="button"
              onClick={() => setCandidateId(null)}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-3 cursor-pointer"
            >
              <ArrowLeft size={12} /> Back to search
            </button>

            {sourceLoading || !source ? (
              <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <MergeSideCard label="Kept" attendee={target} />
                  <MergeSideCard label="Deleted" attendee={source} destructive />
                </div>

                {overlappingCourses.length > 0 && (
                  <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 mb-4 text-xs text-amber-800">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span>
                      Both records already have a booking for <strong>{overlappingCourses.join(', ')}</strong>. That
                      can mean these are two different people, not one — check before merging.
                    </span>
                  </div>
                )}

                <p className="text-sm text-gray-500 mb-4">
                  <strong>{source.fullName}</strong>'s {source.bookings.length} booking{source.bookings.length === 1 ? '' : 's'} and{' '}
                  {source.certificates.length} certificate{source.certificates.length === 1 ? '' : 's'} will move to{' '}
                  <strong>{target.fullName}</strong>, then the <strong>{source.fullName}</strong> record is permanently
                  deleted. This can't be undone.
                </p>

                {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setCandidateId(null)} disabled={merge.isPending}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    disabled={merge.isPending}
                    onClick={() => merge.mutate(source.id)}
                  >
                    {merge.isPending ? 'Merging…' : `Yes, delete ${source.fullName}`}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default function AttendeeDetailPage() {
  const { formatDate } = useAdminDates()
  const fmtDate = (date: string) => {
    return formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  const { id } = useParams<{ id: string }>()
  const [showMerge, setShowMerge] = useState(false)

  const { data: attendee, isLoading, isError } = useQuery({
    queryKey: ['attendee', id],
    queryFn: () => adminApi.getAttendee(id!).then(r => r.data),
    enabled: !!id,
  })

  useTopBar(
    <Breadcrumb items={[
      { label: 'Attendees', to: '/admin/attendees' },
      { label: attendee?.fullName ?? '…' },
    ]} />
  )

  if (isLoading) return <p className="text-gray-500">Loading…</p>
  if (isError || !attendee) return <p className="text-red-500">Failed to load attendee.</p>

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <AdminAttendeeAvatar attendee={attendee} size={56} />
            <h2 className="text-xl font-semibold text-gray-900">{attendee.fullName}</h2>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><Mail size={14} /> {attendee.email}</span>
            {attendee.phone && <span className="flex items-center gap-1.5"><Phone size={14} /> {attendee.phone}</span>}
            {attendee.companyName && <span className="flex items-center gap-1.5"><Building2 size={14} /> {attendee.companyName}</span>}
          </div>
          <div className="mt-2">
            {attendee.marketingConsent
              ? <span className="inline-flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5"><Check size={12} /> Marketing consent given{attendee.marketingConsentAt ? ` · ${fmtDate(attendee.marketingConsentAt)}` : ''}</span>
              : <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5">No marketing consent</span>}
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowMerge(true)}>
          <GitMerge size={15} /> Merge duplicate
        </Button>
      </div>

      {/* Booking history */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Booking history</CardTitle></CardHeader>
        <CardContent>
          {attendee.bookings.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">No bookings recorded.</p>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <th className="px-2 py-2">Course</th>
                    <th className="px-2 py-2">Session date</th>
                    <th className="px-2 py-2">Booking</th>
                    <th className="px-2 py-2">Attendance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {attendee.bookings.map(b => (
                    <tr key={b.sessionAttendeeId}>
                      <td className="px-2 py-2.5 font-medium text-gray-900">{b.courseName}</td>
                      <td className="px-2 py-2.5 text-gray-500 whitespace-nowrap">{fmtDate(b.startsAt)}</td>
                      <td className="px-2 py-2.5 text-gray-500">{b.bookingStatus}</td>
                      <td className="px-2 py-2.5"><AttendanceBadge status={b.attendanceStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Certificates */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Certificates</CardTitle></CardHeader>
        <CardContent>
          {attendee.certificates.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">No certificates issued yet.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {attendee.certificates.map(c => (
                <div key={c.certificateId} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.courseName}</p>
                    <p className="text-xs text-gray-400">Issued {fmtDate(c.issuedAt)}</p>
                  </div>
                  <a href={certificateDownloadUrl(c.certificateId)} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs">
                      <Download size={13} /> Download
                    </Button>
                  </a>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showMerge && <MergeModal target={attendee} onClose={() => setShowMerge(false)} />}
    </div>
  )
}
